import { ArrowUpRight, BellRing, Heart, Mail, X } from "lucide-react";
import { useEffect } from "react";
import ShareActions from "./ShareActions";

function buildShareUrl(vehicles) {
  const url = new URL("/merkliste", window.location.origin);
  url.searchParams.set("f", vehicles.map((vehicle) => vehicle.id).join(","));
  return url.toString();
}

function ChangeHint({ change }) {
  if (!change) return null;
  if (change.type === "price") {
    const cheaper = change.direction === "down";
    return <span className={`favorites-change ${cheaper ? "is-down" : "is-up"}`} data-testid="favorite-change-price"><BellRing size={12} /> Preis {cheaper ? "gesenkt" : "erhöht"}: <s>{change.from}</s> → <strong>{change.to}</strong></span>;
  }
  return <span className="favorites-change" data-testid="favorite-change-availability"><BellRing size={12} /> Verfügbarkeit geändert: <s>{change.from}</s> → <strong>{change.to}</strong></span>;
}

function FavoriteItem({ vehicle, change, onOpenVehicle, onPrepareInquiry, onRemove }) {
  return (
    <li className={`favorites-item ${change ? "has-change" : ""}`} data-testid={`favorite-item-${vehicle.id}`}>
      <button className="favorites-thumb" type="button" onClick={() => onOpenVehicle(vehicle)} aria-label={`${vehicle.brand} ${vehicle.title} öffnen`} data-testid={`favorite-open-${vehicle.id}`}><img src={vehicle.image} alt="" /></button>
      <div className="favorites-copy">
        <span className="mono-label">{vehicle.brand} · {vehicle.year}</span>
        <h3>{vehicle.title}</h3>
        <div className="favorites-meta"><strong>{vehicle.price}</strong><span>{vehicle.mileage}</span><span>{vehicle.fuel}</span><span>{vehicle.gearbox}</span></div>
        <ChangeHint change={change} />
        <div className="favorites-actions">
          <button className="text-action" type="button" onClick={() => onOpenVehicle(vehicle)} data-testid={`favorite-details-${vehicle.id}`}>Details <ArrowUpRight size={14} /></button>
          <button className="text-action" type="button" onClick={() => onPrepareInquiry(vehicle)} data-testid={`favorite-inquiry-${vehicle.id}`}>Anfrage <Mail size={14} /></button>
          {onRemove && <button className="favorites-remove" type="button" onClick={() => onRemove(vehicle.id)} data-testid={`favorite-remove-${vehicle.id}`}>Entfernen</button>}
        </div>
      </div>
    </li>
  );
}

function MissingItem({ entry, onRemove }) {
  return (
    <li className="favorites-item is-missing" data-testid={`favorite-missing-${entry.id}`}>
      <div className="favorites-thumb favorites-thumb-missing" aria-hidden="true"><Heart size={20} strokeWidth={1.3} /></div>
      <div className="favorites-copy">
        <span className="mono-label">NICHT MEHR IM BESTAND</span>
        <h3>{entry.title}</h3>
        <span className="favorites-change is-gone" data-testid="favorite-change-gone"><BellRing size={12} /> Dieses Fahrzeug ist seit Ihrem letzten Besuch nicht mehr verfügbar (zuletzt {entry.price}).</span>
        <div className="favorites-actions"><button className="favorites-remove" type="button" onClick={() => onRemove(entry.id)} data-testid={`favorite-remove-${entry.id}`}>Aus Merkliste entfernen</button></div>
      </div>
    </li>
  );
}

export default function FavoritesDrawer({ vehicles, missing = [], changes = {}, pendingCount, sharedVehicles, onAdoptShared, onShowOwn, onClose, onRemove, onOpenVehicle, onPrepareInquiry }) {
  const isShared = Array.isArray(sharedVehicles);
  const list = isShared ? sharedVehicles : vehicles;
  const changeCount = isShared ? 0 : Object.keys(changes).length + missing.length;

  useEffect(() => {
    const closeOnEscape = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", closeOnEscape);
    document.body.classList.add("modal-open");
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.body.classList.remove("modal-open");
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop drawer-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside className="favorites-drawer" role="dialog" aria-modal="true" aria-labelledby="favorites-title" data-testid={isShared ? "shared-favorites-drawer" : "favorites-drawer"}>
        <div className="favorites-header">
          <div>
            <span className="section-kicker">{isShared ? "GETEILTE MERKLISTE" : "MERKLISTE"}</span>
            <h2 id="favorites-title">{isShared ? <>Geteilte <em>Auswahl.</em></> : <>Deine <em>Favoriten.</em></>}</h2>
            <p data-testid="favorites-count">{list.length === 1 ? "1 Fahrzeug" : `${list.length} Fahrzeuge`}{isShared ? " · per Link mit dir geteilt" : " gemerkt · auf diesem Gerät gespeichert"}</p>
          </div>
          <button className="modal-close" type="button" aria-label="Merkliste schließen" onClick={onClose} data-testid="favorites-close-button"><X size={20} strokeWidth={1.5} /></button>
        </div>
        {changeCount > 0 && <div className="favorites-alert" role="status" data-testid="favorites-change-alert"><BellRing size={15} /><span>{changeCount === 1 ? "1 Änderung" : `${changeCount} Änderungen`} seit Ihrem letzten Besuch – Preis oder Verfügbarkeit gemerkter Fahrzeuge hat sich geändert.</span></div>}
        {isShared && <div className="favorites-shared-actions" data-testid="shared-favorites-actions">
          <button className="button button-primary" type="button" disabled={list.length === 0} onClick={onAdoptShared} data-testid="shared-favorites-adopt-button"><Heart size={15} /> In meine Merkliste übernehmen</button>
          <button className="text-action" type="button" onClick={onShowOwn} data-testid="shared-favorites-show-own-button">Eigene Merkliste anzeigen <ArrowUpRight size={14} /></button>
        </div>}
        {!isShared && vehicles.length > 0 && <div className="favorites-share"><ShareActions idPrefix="favorites" label="MERKLISTE TEILEN" title="Meine Moselcars-Merkliste" text={`Schau dir meine Merkliste bei Moselcars an (${vehicles.length} ${vehicles.length === 1 ? "Fahrzeug" : "Fahrzeuge"}):`} url={buildShareUrl(vehicles)} /></div>}
        {list.length === 0 && missing.length === 0 ? (
          <div className="favorites-empty" data-testid="favorites-empty-state">
            <Heart size={28} strokeWidth={1.2} />
            <strong>{isShared ? "Diese Fahrzeuge sind nicht mehr im Bestand." : "Noch keine Fahrzeuge gemerkt."}</strong>
            <p>{isShared ? "Der geteilte Link enthält keine aktuell verfügbaren Fahrzeuge mehr." : pendingCount > 0 ? "Deine gemerkten Fahrzeuge werden geladen oder sind nicht mehr im Bestand." : "Tippe auf das Herz an einem Fahrzeug, um es hier zu sammeln und später wiederzufinden."}</p>
          </div>
        ) : (
          <ul className="favorites-list">
            {list.map((vehicle) => <FavoriteItem key={vehicle.id} vehicle={vehicle} change={isShared ? null : changes[vehicle.id]} onOpenVehicle={onOpenVehicle} onPrepareInquiry={onPrepareInquiry} onRemove={isShared ? null : onRemove} />)}
            {!isShared && missing.map((entry) => <MissingItem key={entry.id} entry={entry} onRemove={onRemove} />)}
          </ul>
        )}
      </aside>
    </div>
  );
}
